export type StripeSetupFlowKey = 'ar' | 'ap';
export type StripeSetupFlowType = 1 | 2;

export interface StripeFlowContract {
  flowType: StripeSetupFlowType;
  flowLabel: 'AR' | 'AP';
  ensureNameFallback: string;
  setupPendingMessage: string;
  setupStartedToast: string;
  setupStartedStatusMessage: string;
  setupClientSecretCapturedMessage: string;
  setupIntentPayload: (ctx: { appEntityId: string; appBankId: string; flowType: StripeSetupFlowType }) => any;
}

export interface RuntimeSetupIntentValues {
  setupIntentId: string;
  setupIntentStatus: string;
  setupIntentClientSecret: string;
  hostedVerificationUrl: string;
}

export const STRIPE_FLOW_CONTRACTS: Record<StripeSetupFlowKey, StripeFlowContract> = {
  ar: {
    flowType: 1,
    flowLabel: 'AR',
    ensureNameFallback: '',
    setupPendingMessage: 'AR pull intent created. Complete Stripe authorization, then use Refresh setup status.',
    setupStartedToast: 'AR rail setup started. Complete Stripe verification, then refresh setup status.',
    setupStartedStatusMessage: 'AR setup started. Complete verification and refresh setup status.',
    setupClientSecretCapturedMessage: 'AR pull intent created. Client secret received. Click Verify with Stripe to complete bank authorization.',
    setupIntentPayload: ({appEntityId, appBankId, flowType}) => ({
      appEntityId,
      stripeFlowType: flowType,
      appBankId,
      metadataReferenceId: null,
    }),
  },
  ap: {
    flowType: 2,
    flowLabel: 'AP',
    ensureNameFallback: 'AP Vendor',
    setupPendingMessage: 'AP setup intent created. Complete Stripe verification, then refresh setup status.',
    setupStartedToast: 'AP rail setup started. Complete Stripe verification, then refresh setup status.',
    setupStartedStatusMessage: 'AP setup started. Complete verification and refresh setup status.',
    setupClientSecretCapturedMessage: 'AP setup client secret captured. You can now click Verify with Stripe.',
    setupIntentPayload: ({appEntityId, appBankId, flowType}) => ({
      appEntityId,
      appBankId,
      stripeFlowType: flowType,
      paymentMethodTypes: ['us_bank_account'],
      reusableForFuturePayments: true,
      metadataReferenceId: null,
    }),
  },
};

// Flow utilities (role-agnostic helpers to reduce 'ar'|'ap' repetition)
export type FlowKey = StripeSetupFlowKey;
export type FlowMap<T> = Record<FlowKey, T>;
export const FLOWS: ReadonlyArray<FlowKey> = ['ar', 'ap'] as const;

export function forEachFlow(fn: (f: FlowKey) => void): void {
  (FLOWS as ReadonlyArray<FlowKey>).forEach(fn);
}

export function resolveSetupEnvelope(result: any): any {
  if (Array.isArray(result)) {
    return result[0] ?? {};
  }
  return result ?? {};
}

export function getStripeResponseResult(payload: any): any {
  if (!payload) {
    return {};
  }

  if (payload.Result && typeof payload.Result === 'object') {
    return payload.Result;
  }

  const dataNode = payload.data ?? payload.Data;
  if (Array.isArray(dataNode) && dataNode.length > 0) {
    const firstItem = dataNode[0];
    if (firstItem && typeof firstItem === 'object') {
      return firstItem.Result ?? firstItem;
    }
  }

  return payload;
}

function resolveFlowSetupNode(payload: any, flow: StripeSetupFlowType): any {
  const source = resolveSetupEnvelope(payload);
  if (!source || typeof source !== 'object') {
    return null;
  }

  return flow === 1 ? source.Ar || null : flow === 2 ? source.Ap || null : null;
}

export function extractRuntimeSetupIntentValues(flow: StripeSetupFlowType, response: any): RuntimeSetupIntentValues {
  const flowNode = resolveFlowSetupNode(response, flow);
  const resultNode = getStripeResponseResult(response);

  return {
    setupIntentId: (
      flowNode?.SetupIntentId
      ?? flowNode?.setupIntentId
      ?? response?.SetupIntentId
      ?? response?.setupIntentId
      ?? resultNode?.SetupIntentId
      ?? resultNode?.setupIntentId
      ?? ''
    ).toString().trim(),
    setupIntentStatus: (
      flowNode?.SetupIntentStatus
      ?? flowNode?.setupIntentStatus
      ?? flowNode?.Status
      ?? flowNode?.status
      ?? response?.SetupIntentStatus
      ?? response?.setupIntentStatus
      ?? response?.Status
      ?? response?.status
      ?? resultNode?.SetupIntentStatus
      ?? resultNode?.setupIntentStatus
      ?? resultNode?.Status
      ?? resultNode?.status
      ?? ''
    ).toString().trim(),
    setupIntentClientSecret: (
      flowNode?.SetupIntentClientSecret
      ?? flowNode?.setupIntentClientSecret
      ?? flowNode?.ClientSecret
      ?? flowNode?.clientSecret
      ?? flowNode?.client_secret
      ?? response?.SetupIntentClientSecret
      ?? response?.setupIntentClientSecret
      ?? response?.ClientSecret
      ?? response?.clientSecret
      ?? response?.client_secret
      ?? resultNode?.SetupIntentClientSecret
      ?? resultNode?.setupIntentClientSecret
      ?? resultNode?.ClientSecret
      ?? resultNode?.clientSecret
      ?? resultNode?.client_secret
      ?? ''
    ).toString().trim(),
    hostedVerificationUrl: (
      flowNode?.HostedVerificationUrl
      ?? flowNode?.hostedVerificationUrl
      ?? response?.HostedVerificationUrl
      ?? response?.hostedVerificationUrl
      ?? resultNode?.HostedVerificationUrl
      ?? resultNode?.hostedVerificationUrl
      ?? ''
    ).toString().trim(),
  };
}

// Normalizers and status helpers (shared by component/template)
export function normalizeAchStatusValue(value: any): string {
  return (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '');
}

export function isStatusPendingLike(value: any): boolean {
  const normalized = normalizeAchStatusValue(value);
  if (!normalized) {
    return false;
  }
  return normalized === 'pending'
    || normalized === 'processing'
    || normalized === 'inprogress'
    || normalized === 'pendingverification'
    || normalized === 'requiresaction'
    || normalized === 'requirespaymentmethod'
    || normalized === 'requiresconfirmation'
    || normalized === 'requiresmicrodepositverification'
    || normalized === 'microdepositpending'
    || normalized === 'awaitingmicrodeposit'
    || normalized === 'awaitingverification';
}

// Client secret classifier
export function getClientSecretIntentType(clientSecret: string): 'setup' | 'payment' | 'unknown' {
  const normalized = (clientSecret || '').toString().trim().toLowerCase();
  if (!normalized) {
    return 'unknown';
  }
  if (normalized.startsWith('seti_')) {
    return 'setup';
  }
  if (normalized.startsWith('pi_')) {
    return 'payment';
  }
  return 'unknown';
}

// Generic success detection for Stripe step responses
export function isSuccessfulStripeResponse(result: any): boolean {
  const payload = getStripeResponseResult(result);
  if (payload === null || payload === undefined) {
    return false;
  }
  const errorMessage = (payload?.Message ?? payload?.message ?? '')
    .toString()
    .trim()
    .toLowerCase();
  if (errorMessage.includes('fail') || errorMessage.includes('error')) {
    return false;
  }
  if (typeof payload?.Success === 'boolean') {
    return payload.Success;
  }
  if (typeof payload?.IsSuccess === 'boolean') {
    return payload.IsSuccess;
  }
  if (typeof payload?.HasErrors === 'boolean') {
    return !payload.HasErrors;
  }
  return !payload?.Error;
}

// DTO mappers (moved from component for reuse and testability)
export function mapStripeFlowDto(flow: any) {
  const source = flow ?? {};
  const setupIntentClientSecret = (
    source.SetupIntentClientSecret
    ?? source.ClientSecret
    ?? source.clientSecret
    ?? source.client_secret
    ?? ''
  ).toString().trim();
  return {
    enabled: Boolean(source.Enabled),
    isActive: Boolean(source.IsActive),
    stripePaymentMethodId: source.DefaultPaymentMethodId ?? '',
    stripeCustomerId: source.StripeCustomerId ?? '',
    methodType: source.MethodType ?? '',
    bankName: source.BankName ?? '',
    last4: source.Last4 ?? '',
    achStatus: source.AchStatus ?? '',
    hostedVerificationUrl: source.HostedVerificationUrl ?? '',
    setupIntentId: source.SetupIntentId ?? '',
    setupIntentStatus: source.SetupIntentStatus ?? '',
    setupIntentClientSecret,
    connectedAccountId: source.ConnectedAccountId ?? '',
    bankAccountId: source.BankAccountId ?? '',
    paymentIntentId: source.PaymentIntentId ?? '',
    verifiedAtUtc: source.VerifiedAtUtc ?? null,
  };
}

export function mapStripeGlobalDto(globalSource: any) {
  const source = globalSource ?? {};
  const reusableForFuturePaymentsSource = source.IsReusableForFuturePayments;
  return {
    id: (source.Id ?? '').toString().trim(),
    bankId: (source.BankId ?? '').toString().trim(),
    appBankId: (source.AppBankId ?? '').toString().trim(),
    appEntityId: (source.AppEntityId ?? '').toString().trim(),
    companyId: (source.CompanyId ?? '').toString().trim(),
    bankDisplayName: (source.BankDisplayName ?? '').toString().trim(),
    stripeBankName: (source.StripeBankName ?? '').toString().trim(),
    bankLast4: (source.BankLast4 ?? '').toString().trim(),
    bankAccountType: (source.BankAccountType ?? '').toString().trim(),
    bankAccountHolderType: (source.BankAccountHolderType ?? '').toString().trim(),
    bankFingerprint: (source.BankFingerprint ?? '').toString().trim(),
    bankNetworkPreferred: (source.BankNetworkPreferred ?? '').toString().trim(),
    stripePaymentMethodType: (source.StripePaymentMethodType ?? '').toString().trim(),
    isReusableForFuturePayments: reusableForFuturePaymentsSource === undefined || reusableForFuturePaymentsSource === null
      ? true
      : Boolean(reusableForFuturePaymentsSource),
    createdUtc: source.CreatedUtc ?? null,
    updatedUtc: source.UpdatedUtc ?? null,
  };
}

// Payload builders
export function buildEnsureCustomerPayload(
  flowType: StripeSetupFlowType,
  appEntityId: string,
  selectedEntity: any,
  achEmailValue?: string,
  ensureNameFallback?: string,
): any {
  const email = (
    selectedEntity?.email
    ?? selectedEntity?.Email
    ?? achEmailValue
    ?? ''
  ).toString().trim();

  const name = (
    selectedEntity?.name
    ?? selectedEntity?.Name
    ?? ensureNameFallback
    ?? ''
  ).toString().trim();

  return {
    appEntityId,
    stripeFlowType: flowType,
    email,
    name,
  };
}

// ACH status view model helper
export type AchStatusView = {
  code: string;          // normalized code used for comparisons (lowercase, no spaces)
  label: string;         // human-friendly label to display
  badgeClass: string;    // bootstrap-like classes used by the template
  isVerified: boolean;   // convenience flag
};

export type MinimalFlowState = {
  achStatus?: string;
  setupIntentStatus?: string;
  setupIntentId?: string;
  setupIntentClientSecret?: string;
  hostedVerificationUrl?: string;
};

export type VerificationGate = {
  showInstant: boolean;
  showMicrodeposit: boolean;
  isLocked?: boolean; // optional; not needed for current status rendering
};

export function computeAchStatusView(flowState: MinimalFlowState | null | undefined, gate: VerificationGate): AchStatusView {
  // Defaults
  const empty: AchStatusView = {
    code: 'requiressetup',
    label: 'Requires setup',
    // Non-subtle warning style for not-setup on initial load
    badgeClass: 'bg-light text-warning border-warning',
    isVerified: false,
  };

  if (!flowState) {
    return empty;
  }

  const achExact = (flowState.achStatus ?? '').toString().trim();
  if (achExact === 'Verified') {
    return {
      code: 'verified',
      label: 'Verified',
      badgeClass: 'bg-light text-success border-success',
      isVerified: true,
    };
  }

  // Linear gate labels (AR semantics applied to both)
  if (gate?.showInstant) {
    return {
      code: 'instantverify',
      label: 'Instant Verify',
      badgeClass: 'bg-light text-warning border-warning',
      isVerified: false,
    };
  }
  if (gate?.showMicrodeposit) {
    return {
      code: 'microdepositverify',
      label: 'Microdeposit Verify',
      badgeClass: 'bg-light text-warning border-warning',
      isVerified: false,
    };
  }

  const explicit = (achExact || (flowState.setupIntentStatus ?? '')).toString().trim();
  if (explicit) {
    const code = normalizeAchStatusValue(explicit);
    // Map to classes similar to previous logic
    let badgeClass = 'bg-light text-secondary border-secondary';
    const notSetupCodes = new Set([
      'requiressetup', 'none', 'notset', 'notsetup', 'uninitialized', 'unknown', 'na', 'n/a', 'notconfigured', 'notconfiguredyet', 'noaccount', 'missing'
    ]);
    if (notSetupCodes.has(code)) {
      // Non-subtle warning style for not-setup explicit statuses
      badgeClass = 'bg-light text-warning border-warning';
    } else if (isStatusPendingLike(code)) {
      badgeClass = 'bg-light text-warning border-warning';
    }
    return {
      code,
      label: explicit,
      badgeClass,
      isVerified: false,
    };
  }

  // Verification context present but not explicit status
  const hasContext = Boolean((flowState.setupIntentId ?? '').toString().trim())
    || Boolean((flowState.setupIntentClientSecret ?? '').toString().trim())
    || Boolean((flowState.hostedVerificationUrl ?? '').toString().trim());
  if (hasContext) {
    return {
      code: 'verificationrequired',
      label: 'Verification Required',
      badgeClass: 'bg-light text-warning border-warning',
      isVerified: false,
    };
  }

  return empty;
}
